import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card } from '../components/ui/Card';
import { DataTable, TableToolbar, Pagination } from '../components/ui/DataTable';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../components/ui/AsyncBoundary';
import { SearchInput, Select } from '../components/ui/Field';
import { SegmentedControl, Button } from '../components/ui/Button';
import { FleetMap } from '../components/map/FleetMap';
import { vehicleColumns, dynamicColumns } from '../components/domain/columns';
import { AttributePicker } from '../components/domain/AttributePicker';
import { CreateVehicleModal } from '../components/domain/VehicleModals';
import { Can } from '../auth/Can';
import { useResource, usePagedList } from '../hooks/useResource';
import { useFleetLive } from '../hooks/useFleetLive';
import { usePreference } from '../hooks/usePreferences';
import { DEFAULT_COLUMNS } from '../lib/attributes';
import { VEHICLE_STATES, VEHICLE_STATE_ORDER } from '../lib/domain';

/** How many pins the map will draw before it stops pretending to be readable. */
const MAP_LIMIT = 1000;

/**
 * The fleet grid, with a map view over the same data and the same filters.
 *
 * The two views want different amounts of data, and pretending otherwise is
 * what breaks at scale. The list pages on the server — fifty rows, a total,
 * and the search sent as a query parameter — so a tenant with two thousand
 * vehicles costs the same as one with twenty. The map genuinely needs every
 * matching vehicle at once and is capped instead, because a thousand
 * overlapping pins is not a map; past that the answer is to filter, and the
 * page says so rather than quietly drawing a subset.
 *
 * Both views send the same filters to the same endpoint, so switching between
 * them never changes what you are looking at.
 */
export function Vehicles() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [view, setView] = useState('table');
  const [state, setState] = useState(params.get('state') || '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const fleetId = params.get('fleetId') || '';

  const filters = { fleetId: fleetId || undefined, state: state || undefined };

  const list = usePagedList('/api/vehicles', filters, { limit: 50, pollMs: 60_000 });

  // Only fetched while the map is on screen, and never more than the cap.
  const mapResource = useResource(
    view === 'map' ? '/api/vehicles' : null,
    { ...filters, q: list.query.trim() || undefined, limit: MAP_LIMIT },
    { pollMs: 60_000 }
  );

  const fleets = useResource('/api/fleets');
  // What this organisation's devices actually report — the source of the
  // column picker, discovered rather than configured.
  const catalog = useResource('/api/attributes');
  const [columnKeys, setColumnKeys] = usePreference('vehicle_columns', DEFAULT_COLUMNS);

  // Live socket updates are applied to whichever set is on screen.
  const { vehicles: liveRows } = useFleetLive(list.rows);
  const { vehicles: liveMapRows } = useFleetLive(mapResource.data?.vehicles);

  const chosenColumns = useMemo(
    () => dynamicColumns(columnKeys, catalog.data?.attributes),
    [columnKeys, catalog.data]
  );

  const setFleet = (value) => {
    const next = new URLSearchParams(params);
    if (value) next.set('fleetId', value);
    else next.delete('fleetId');
    setParams(next, { replace: true });
  };

  const filtered = Boolean(list.query || state || fleetId);
  const mapTruncated = view === 'map' && (mapResource.data?.total ?? 0) > MAP_LIMIT;

  return (
    <Page className="space-y-4">
      <PageHeader
        title="Vehicles"
        subtitle={
          list.loading && !list.total
            ? 'Loading…'
            : filtered
              ? `${list.total.toLocaleString()} match`
              : `${list.total.toLocaleString()} in your scope`
        }
        actions={
          <>
            <SegmentedControl
              value={view}
              onChange={setView}
              options={[
                { value: 'table', label: 'List' },
                { value: 'map', label: 'Map' },
              ]}
            />
            {view === 'table' && (
              <Button variant="secondary" size="sm" icon="table" onClick={() => setPickerOpen(true)}>
                Columns
                {columnKeys.length > 0 && (
                  <span className="ml-0.5 rounded bg-surface-3 px-1 text-[11px] tabular-nums text-ink-3">
                    {columnKeys.length}
                  </span>
                )}
              </Button>
            )}
            <Can do="vehicle:create">
              <Button variant="accent" size="sm" icon="plus" onClick={() => setCreating(true)}>
                Add vehicle
              </Button>
            </Can>
          </>
        }
      />

      <Card className="overflow-hidden">
        {/* Filters sit in one row above the content, in the same place on
            every screen that has them. */}
        <TableToolbar>
          <SearchInput
            value={list.query}
            onChange={list.setQuery}
            placeholder="Registration or model"
            className="w-full sm:w-64"
          />

          <Select
            size="sm"
            value={state}
            onChange={(event) => setState(event.target.value)}
            placeholder="Any status"
            className="w-40"
            options={VEHICLE_STATE_ORDER.map((key) => ({
              value: key,
              label: VEHICLE_STATES[key].label,
            }))}
          />

          {fleets.data?.fleets?.length > 1 && (
            <Select
              size="sm"
              value={fleetId}
              onChange={(event) => setFleet(event.target.value)}
              placeholder="All fleets"
              className="w-48"
              options={fleets.data.fleets.map((fleet) => ({ value: fleet.id, label: fleet.name }))}
            />
          )}

          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              icon="close"
              onClick={() => {
                list.setQuery('');
                setState('');
                setFleet('');
              }}
            >
              Clear
            </Button>
          )}
        </TableToolbar>

        <AsyncBoundary
          state={view === 'map' ? mapResource : list}
          skeleton={<SkeletonRows rows={8} />}
          isEmpty={() => (view === 'map' ? liveMapRows.length === 0 : list.rows.length === 0)}
          empty={
            <EmptyState
              icon="truck"
              title={filtered ? 'Nothing matches those filters' : 'No vehicles in your scope'}
              description={
                filtered
                  ? 'Try clearing the search or choosing a different status.'
                  : 'Vehicles appear here once they are added to a fleet you can see.'
              }
            />
          }
        >
          {() =>
            view === 'map' ? (
              <div className="p-3">
                {mapTruncated && (
                  <p className="mb-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-[12.5px] text-warning-ink">
                    Showing the first {MAP_LIMIT.toLocaleString()} of{' '}
                    {mapResource.data.total.toLocaleString()} vehicles. Filter by fleet or status to
                    see a map you can read.
                  </p>
                )}
                <FleetMap
                  vehicles={liveMapRows}
                  onSelect={(id) => navigate(`/vehicles/${id}`)}
                  height={560}
                />
              </div>
            ) : (
              <>
                <DataTable
                  columns={vehicleColumns({ dynamic: chosenColumns })}
                  rows={liveRows}
                  onRowClick={(row) => navigate(`/vehicles/${row.id}`)}
                  defaultSort={{ key: 'reg_number', direction: 'asc' }}
                  paged={list.total > list.page.limit}
                />
                <Pagination page={list.page} onChange={list.setOffset} unit="vehicles" />
              </>
            )
          }
        </AsyncBoundary>
      </Card>

      <AttributePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        attributes={catalog.data?.attributes}
        loading={catalog.loading}
        selected={columnKeys}
        onChange={setColumnKeys}
      />

      <CreateVehicleModal
        open={creating}
        onClose={() => setCreating(false)}
        fleets={fleets.data?.fleets || []}
        onCreated={() => {
          setCreating(false);
          list.refetch();
        }}
      />
    </Page>
  );
}
